import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Star,
  Share2,
  CheckCircle2,
  Music,
} from "lucide-react";

import Loader from "../common/Loader";
import NotFound from "../common/NotFound";
import { getTemplateByIdRequest } from "../store/slice/appSlice";
import { toggleLoginModal } from "../store/slice/authSlice";

export default function TemplateDetails() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    getTemplateByIdData: template,
    getTemplateByIdLoading,
    getTemplateByIdError,
  } = useSelector((state) => state.app);

  const { isLoggedIn } = useSelector((state) => state.auth);

  useEffect(() => {
    if (slug) {
      dispatch(getTemplateByIdRequest({ slug }));
    }
  }, [dispatch, slug]);

  const handleCustomize = () => {
    if (isLoggedIn) {
      navigate(`/editor/${slug}`);
    } else {
      dispatch(toggleLoginModal(true));
    }
  };

  /* ---------------- LOADING ---------------- */
  if (getTemplateByIdLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader text="Loading Template Details..." />
      </div>
    );
  }

  /* ---------------- ERROR ---------------- */
  if (getTemplateByIdError || !template) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <NotFound message="Oops! Template not found." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-24 font-sans">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        
        {/* Responsive Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">

          {/* -------- LEFT : PREVIEW -------- */}
          <div className="lg:col-span-5 flex justify-center">
            <div className="relative w-full max-w-[320px] sm:max-w-[360px]">
              
              <div className="relative rounded-2xl border border-gray-200 bg-white shadow-xl overflow-hidden">
                <div className="aspect-[9/16] bg-gray-100">
                  {template.preview_file_url ? (
                    template.preview_file_url.endsWith(".mp4") ? (
                      <video
                        src={template.preview_file_url}
                        controls
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img
                        src={template.preview_file_url}
                        alt={template.title}
                        className="w-full h-full object-cover"
                      />
                    )
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                      <Music className="w-12 h-12 opacity-50" />
                      <span className="text-sm">No Preview</span>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>

          {/* -------- RIGHT : DETAILS -------- */}
          <div className="lg:col-span-7 space-y-8">

            {/* Title */}
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 leading-tight">
                {template.title || "Untitled Template"}
              </h1>

              <p className="text-gray-600 mt-4 leading-relaxed text-sm sm:text-base">
                This elegant invitation video is the perfect way to invite your
                guests with style. Fully customizable with names, dates, venue,
                and more.
              </p>
            </div>

            {/* Languages */}
            <div className="p-4 bg-white border border-gray-200 rounded-xl shadow-sm">
              <p className="text-base sm:text-lg text-gray-800">
                <span className="font-semibold mr-2">Create In:</span>
                English, हिन्दी
                {/* ગુજરાતી, ਪੰਜਾਬੀ, தமிழ், తెలుగు */}
              </p>
            </div>

            {/* Pricing Section */}
            <div className="space-y-4">

              {/* Free */}
              <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-500 font-bold">
                  0
                </div>
                <div>
                  <div className="text-lg font-bold text-red-600">Free</div>
                  <div className="text-sm text-red-800/70">
                    With Watermark & Low Quality
                  </div>
                </div>
              </div>

              {/* Paid */}
              <div className="relative flex items-center gap-3 p-4 bg-green-50 border border-green-100 rounded-xl">
                <div className="absolute top-0 right-0 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-bl-lg uppercase">
                  Recommended
                </div>

                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                  <CheckCircle2 className="w-6 h-6" />
                </div>

               <div>
                <div className="text-lg font-bold text-gray-900">
                  Pay{" "}

                  {template?.template_price_old && (
                    <span className="text-gray-400 line-through mr-2">
                      ₹ {template.template_price_old}
                    </span>
                  )}

                  <span className="text-blue-600">
                    ₹ {template?.template_price}
                  </span>
                </div>

                <div className="text-sm text-gray-600">
                  HD Quality & No Watermark
                </div>
              </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-4 pt-2">
              <button
                onClick={handleCustomize}
                className="w-full py-5 bg-gradient-to-r from-[#F4C430] to-yellow-500 hover:to-yellow-600 text-gray-900 rounded-2xl font-bold text-lg shadow-lg transition transform active:scale-95"
              >
                Create Now
              </button>

              {/* <div className="flex gap-3">
                <button className="flex-1 py-4 bg-white border border-gray-300 rounded-xl font-medium text-gray-600 hover:bg-gray-50 transition">
                  Add to Wishlist
                </button>

                <button className="flex-1 py-4 bg-white border border-gray-300 rounded-xl font-medium text-gray-600 hover:bg-gray-50 transition flex items-center justify-center gap-2">
                  <Share2 className="w-5 h-5" />
                  Share
                </button>
              </div> */}
            </div>

            {/* Features */}
            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-6">
                What You Get
              </h3>

              <ul className="space-y-4">
                <li className="flex gap-3">
                  <Star className="w-5 h-5 text-yellow-500 fill-current mt-1" />
                  <span className="text-gray-700 text-sm sm:text-base">
                    Customize names, dates, venue & details.
                  </span>
                </li>

                <li className="flex gap-3">
                  <Star className="w-5 h-5 text-yellow-500 fill-current mt-1" />
                  <span className="text-gray-700 text-sm sm:text-base">
                    No watermark in paid version.
                  </span>
                </li>

                <li className="flex gap-3">
                  <Star className="w-5 h-5 text-yellow-500 fill-current mt-1" />
                  <span className="text-gray-700 text-sm sm:text-base">
                    Instant video generation within minutes.
                  </span>
                </li>
              </ul>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
